import type { CardFontDef } from './fontRegistry';

/**
 * AW6 — 埋め込み日本語フォントのローダ (card-print)。old2 `fontLoader.ts` を一般化。
 *
 * フォント定義 (URL / family) は `fontRegistry.ts` が持つ。本モジュールは glyf TrueType を
 * CDN から fetch し、jsPDF の `addFileToVFS` / `addFont` に渡せる base64 として公開する。
 * フォントは複数 (ゴシック/明朝/等幅) を id 別にキャッシュする。
 *
 * フォントは git に同梱せず PDF 生成/プレビュー時のみ fetch (force-cache)。取得失敗時 (CDN 不通 /
 * CSP) は throw → 呼出側が helvetica fallback で続行 (英数字のみ正常)。jsPDF は save() 時に
 * サブセット化するため最終 PDF は実使用グリフのみ (通常 100-300KB)。
 *
 * glyf TrueType 限定の理由・各フォントの検証結果は fontRegistry.ts のヘッダ参照。
 */

export interface LoadedFont {
  /** jsPDF に登録する family 名 (CardFontDef.pdfFamily)。 */
  family: string;
  base64: string;
}

// URL 別キャッシュ (Regular/Bold を別エントリで重複 fetch 抑制)。
// catch はキャッシュしない (一過性失敗を再試行可能に)。
const cache = new Map<string, LoadedFont>();
const inflight = new Map<string, Promise<LoadedFont>>();

function arrayBufferToBase64(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  const chunkSize = 0x8000;
  let binary = '';
  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.subarray(i, Math.min(i + chunkSize, bytes.length));
    binary += String.fromCharCode.apply(null, Array.from(chunk));
  }
  return btoa(binary);
}

async function loadByUrl(url: string, family: string): Promise<LoadedFont> {
  const hit = cache.get(url);
  if (hit) return hit;
  const running = inflight.get(url);
  if (running) return running;
  const promise = (async () => {
    const res = await fetch(url, { cache: 'force-cache' });
    if (!res.ok) {
      throw new Error(`フォントの読込に失敗しました (HTTP ${res.status})`);
    }
    const buf = await res.arrayBuffer();
    const loaded: LoadedFont = { family, base64: arrayBufferToBase64(buf) };
    cache.set(url, loaded);
    return loaded;
  })();
  inflight.set(url, promise);
  try {
    return await promise;
  } finally {
    inflight.delete(url);
  }
}

/** Regular ウェイトを読み込む。fetch 失敗時は throw (caller が helvetica fallback で続行)。 */
export async function loadFont(def: CardFontDef): Promise<LoadedFont> {
  return loadByUrl(def.url, def.pdfFamily);
}

/** Bold ウェイトを読み込む。boldUrl が無いフォントは null (太字は擬似表現)。 */
export async function loadFontBold(def: CardFontDef): Promise<LoadedFont | null> {
  if (!def.boldUrl) return null;
  return loadByUrl(def.boldUrl, def.pdfFamily);
}

/**
 * jsPDF document にフォントを登録 (new jsPDF ごとに 1 回)。
 * Regular を 'normal' に登録。`bold` があれば 'bold' に **本物の Bold** を登録し、
 * 無ければ 'bold' にも Regular を割当 (太字は描画側で輪郭線により擬似表現)。
 */
export function registerFont(
  pdf: {
    addFileToVFS: (n: string, d: string) => void;
    addFont: (file: string, family: string, style: string) => void;
  },
  regular: LoadedFont,
  bold?: LoadedFont | null,
): void {
  const regFile = `${regular.family}.ttf`;
  pdf.addFileToVFS(regFile, regular.base64);
  pdf.addFont(regFile, regular.family, 'normal');
  if (bold) {
    const boldFile = `${regular.family}-Bold.ttf`;
    pdf.addFileToVFS(boldFile, bold.base64);
    pdf.addFont(boldFile, regular.family, 'bold');
  } else {
    pdf.addFont(regFile, regular.family, 'bold');
  }
}
